import { useCallback, useContext, useEffect, useRef } from 'react';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import { useUserLocation } from '~/context/common/UserContext';
import InventoryContext, {
  useRequestIds
} from '~/context/srp/InventoryContext';
import isServerSideRendering from '~/utilities/isServerSideRendering';
import useLoadedInventoryVehicles from '../useLoadedInventoryVehicles';

// When users click "back", the browser may restore the previous HTML instead of
// loading anew. This will result in a duplicate search request ID.
function useIsSearchRequestIdDuplicate(): boolean {
  const storageKey = 'srp/pageLoadSearchRequestId';

  const { inventory } = useContext(InventoryContext);
  const initialSearchRequestIdRef = useRef(inventory.data?.searchRequestId);
  const searchRequestIdFromStorageRef = useRef(
    isServerSideRendering ? null : localStorage.getItem(storageKey)
  );

  useEffect(() => {
    localStorage.setItem(storageKey, initialSearchRequestIdRef.current || '');
  }, []);

  return (
    initialSearchRequestIdRef.current ===
      searchRequestIdFromStorageRef.current &&
    initialSearchRequestIdRef.current === inventory.data?.searchRequestId
  );
}

export default function useTrackMegaVehicle() {
  const trackEvent = useTrackEvent();
  const requestIds = useRequestIds();
  const {
    loadedInventoryVehicles,
    loadedSimilarVehicles,
    shouldHaveSimilarVehicles,
    svSearchRequestId
  } = useLoadedInventoryVehicles();
  const { zip } = useUserLocation();
  const duplicateSearchRequestId = useIsSearchRequestIdDuplicate();

  const zipRef = useRef(zip);
  useEffect(() => {
    zipRef.current = zip;
  }, [zip]);

  const trackMegaVehicle = useCallback(() => {
    trackEvent(
      'mega-srp-vehicle-data',
      {
        ...requestIds,
        duplicateSearchRequestId,
        vehicles: loadedInventoryVehicles.vehicles,
        zip: zipRef.current
      },
      undefined,
      {
        useFetch: true,
        fetchOption: { keepalive: false }
      }
    );
  }, [
    duplicateSearchRequestId,
    loadedInventoryVehicles.vehicles,
    requestIds,
    trackEvent
  ]);

  return {
    loadedInventoryVehicles,
    loadedSimilarVehicles,
    shouldHaveSimilarVehicles,
    svSearchRequestId,
    trackMegaVehicle
  };
}
